import { tagged } from "@mirage/service-logging";
import type { KVStorage } from "@mirage/storage";
import LocalStorage from "@mirage/storage/local-storage";
import WithDefaults from "@mirage/storage/with-defaults";
import { v5 as uuidv5 } from "uuid";
import {
  MemoryCache,
  PreviousPrompt,
  PROMPT_TYPE,
  RelevanceRubric,
} from "./types";

export const RELEVANCE_PROMPT_NAMESPACE =
  "c7549bdc-035a-11ef-be37-325096b39f47";

const logger = tagged("compute-relevance-cache");

export type PersistentCacheShape = {
  prompts: PreviousPrompt[];
  defaultPromptIds: { [key in RelevanceRubric]?: string };
};

const defaults: PersistentCacheShape = {
  prompts: [],
  defaultPromptIds: {},
};

export class RecentPromptsCache implements MemoryCache<PreviousPrompt> {
  private promptMap: Map<string, PreviousPrompt> = new Map();
  private readonly CACHE_LIMIT = 10;
  private wrappedStorage: WithDefaults<PersistentCacheShape>;

  constructor() {
    const storageAdapter = LocalStorage<PersistentCacheShape>(
      "compute-relevance-prompts",
    );
    this.wrappedStorage = new WithDefaults<PersistentCacheShape>(
      storageAdapter,
      defaults,
    );

    this.wrappedStorage.get("prompts").then((prompts) => {
      // Clear the map and add each prompt with its UUID as the key
      this.promptMap.clear();
      prompts.forEach((prompt) => {
        this.promptMap.set(prompt.uuid, prompt);
      });
      logger.debug(`Loaded ${prompts.length} prompts from persistent storage`);
    });
  }

  all() {
    // Convert map values to array and sort by time (newest first)
    return Array.from(this.promptMap.values()).sort((a, b) => b.time - a.time);
  }

  addItem(item: PreviousPrompt): Promise<PreviousPrompt[]> {
    // Check if we already have a prompt with the same text (case-insensitive)
    const normalizedPrompt = item.prompt.toLowerCase();

    // Remove any existing prompts with the same text or UUID
    for (const [uuid, existingPrompt] of this.promptMap.entries()) {
      if (
        existingPrompt.prompt.toLowerCase() === normalizedPrompt ||
        uuid === item.uuid
      ) {
        this.promptMap.delete(uuid);
      }
    }

    // Add the new item
    this.promptMap.set(item.uuid, item);

    // If we're over the limit, remove the oldest items
    if (this.promptMap.size > this.CACHE_LIMIT) {
      const sortedPrompts = this.all();
      const promptsToRemove = sortedPrompts.slice(this.CACHE_LIMIT);

      promptsToRemove.forEach((prompt) => {
        this.promptMap.delete(prompt.uuid);
      });
    }

    // Update storage
    this.wrappedStorage.set("prompts", this.all());

    return Promise.resolve(this.all());
  }

  removeItem(item: PreviousPrompt): Promise<PreviousPrompt[]> {
    // Remove the item by UUID
    this.promptMap.delete(item.uuid);

    // Update storage
    this.wrappedStorage.set("prompts", this.all());

    return Promise.resolve(this.all());
  }

  clear() {
    // Clear the map
    this.promptMap.clear();

    // Clear storage
    this.wrappedStorage.set("prompts", []);
  }

  count() {
    return this.promptMap.size;
  }

  async getDefaultPromptId(rubric: RelevanceRubric): Promise<string | null> {
    const defaultPromptIds = await this.wrappedStorage.get("defaultPromptIds");
    return defaultPromptIds[rubric] || null;
  }

  async setDefaultPromptId(
    rubric: RelevanceRubric,
    promptId: string | null,
  ): Promise<void> {
    const defaultPromptIds = await this.wrappedStorage.get("defaultPromptIds");
    if (promptId === null) {
      delete defaultPromptIds[rubric];
    } else {
      defaultPromptIds[rubric] = promptId;
    }
    await this.wrappedStorage.set("defaultPromptIds", defaultPromptIds);
  }
}

/**
 * Creates a PreviousPrompt object from a prompt string
 *
 * @param prompt The prompt to convert to a PreviousPrompt
 * @param type The type of prompt (default or custom)
 * @returns A PreviousPrompt object with uuid and query properties
 */
export function createPromptRecord(
  prompt: string,
  type: PROMPT_TYPE = PROMPT_TYPE.CUSTOM,
): PreviousPrompt {
  const time = Date.now();
  return {
    uuid: uuidv5(prompt, RELEVANCE_PROMPT_NAMESPACE),
    prompt,
    time,
    type,
  };
}

/**
 * KVStorage-based implementation of MemoryCache for use in desktop environments
 * where LocalStorage is not available (e.g., Electron main process)
 */
export class KVStoragePromptsCache implements MemoryCache<PreviousPrompt> {
  private promptMap: Map<string, PreviousPrompt> = new Map();
  private readonly CACHE_LIMIT = 10;
  private storage: KVStorage<PersistentCacheShape>;
  private initialized = false;

  constructor(storage: KVStorage<PersistentCacheShape>) {
    this.storage = storage;
    this.initialize();
  }

  private async initialize() {
    if (this.initialized) return;

    try {
      const prompts = (await this.storage.get("prompts")) || [];
      this.promptMap.clear();
      prompts.forEach((prompt) => {
        this.promptMap.set(prompt.uuid, prompt);
      });
      logger.debug(`Loaded ${prompts.length} prompts from KV storage`);
      this.initialized = true;
    } catch (error) {
      logger.error("Error initializing KVStoragePromptsCache", error);
      this.initialized = true; // Mark as initialized even on error to prevent retry loops
    }
  }

  private async ensureInitialized() {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  all() {
    // Convert map values to array and sort by time (newest first)
    return Array.from(this.promptMap.values()).sort((a, b) => b.time - a.time);
  }

  async addItem(item: PreviousPrompt): Promise<PreviousPrompt[]> {
    await this.ensureInitialized();

    // Check if we already have a prompt with the same text (case-insensitive)
    const normalizedPrompt = item.prompt.toLowerCase();

    // Remove any existing prompts with the same text or UUID
    for (const [uuid, existingPrompt] of this.promptMap.entries()) {
      if (
        existingPrompt.prompt.toLowerCase() === normalizedPrompt ||
        uuid === item.uuid
      ) {
        this.promptMap.delete(uuid);
      }
    }

    // Add the new item
    this.promptMap.set(item.uuid, item);

    // If we're over the limit, remove the oldest items
    if (this.promptMap.size > this.CACHE_LIMIT) {
      const sortedPrompts = this.all();
      const promptsToRemove = sortedPrompts.slice(this.CACHE_LIMIT);

      promptsToRemove.forEach((prompt) => {
        this.promptMap.delete(prompt.uuid);
      });
    }

    // Update storage
    await this.storage.set("prompts", this.all());

    return this.all();
  }

  async removeItem(item: PreviousPrompt): Promise<PreviousPrompt[]> {
    await this.ensureInitialized();

    // Remove the item by UUID
    this.promptMap.delete(item.uuid);

    // Update storage
    await this.storage.set("prompts", this.all());

    return this.all();
  }

  clear() {
    // Clear the map
    this.promptMap.clear();

    // Clear storage
    this.storage.set("prompts", []);
  }

  count() {
    return this.promptMap.size;
  }

  async getDefaultPromptId(rubric: RelevanceRubric): Promise<string | null> {
    await this.ensureInitialized();
    const defaultPromptIds = (await this.storage.get("defaultPromptIds")) || {};
    return defaultPromptIds[rubric] || null;
  }

  async setDefaultPromptId(
    rubric: RelevanceRubric,
    promptId: string | null,
  ): Promise<void> {
    await this.ensureInitialized();
    const defaultPromptIds = (await this.storage.get("defaultPromptIds")) || {};
    if (promptId === null) {
      delete defaultPromptIds[rubric];
    } else {
      defaultPromptIds[rubric] = promptId;
    }
    await this.storage.set("defaultPromptIds", defaultPromptIds);
  }
}
